import React from 'react'
import { withRouter } from 'react-router-dom'
import { Button, Card, DatePicker, Empty, message, Select, Skeleton, Table, Tag } from 'antd'
import moment from 'moment'
import { fetchOperationLogs, fetchOperationTypes } from '../../api/clubOperationLogs'
import { fetchTableList } from '../../api/clubCleanTableRecord'

const ALL_VALUE = 'all'
const ALL_LABEL = '全部'

class ClubOperationLogs extends React.Component {
  state = {
    loading: true,
    tableId: ALL_VALUE,
    operationType: '',
    dateRange: [moment(), moment()],
    page: 1,
    pageSize: 10,
    logs: [],
    total: 0,
    tables: [],
    operationTypes: [],
  }

  componentDidMount() {
    this.loadInitialData()
  }

  /** 加载初始数据：球台列表 + 操作类型枚举 + 第一页日志 */
  loadInitialData = async () => {
    try {
      const clubId = sessionStorage.getItem('club_id')
      const [tableRes, typesRes] = await Promise.all([
        fetchTableList({ clubId }),
        fetchOperationTypes(),
      ])

      // 解析球台列表
      const tables = [{ name: ALL_LABEL, tableId: ALL_VALUE }]
      if (tableRes && tableRes.data) {
        const tableList = Array.isArray(tableRes.data) ? tableRes.data : []
        tableList.forEach((item) => {
          tables.push({ name: item.name, tableId: item.table_id || item.tableId })
        })
      }

      // 解析操作类型枚举
      const operationTypes = [{ label: ALL_LABEL, value: '' }]
      if (typesRes && typesRes.data && Array.isArray(typesRes.data)) {
        const items = typesRes.data.map(item =>
          typeof item === 'string' ? { label: item, value: item } : item
        )
        operationTypes.push(...items)
      }

      this.setState({ tables, operationTypes })

      // 加载第一页日志
      await this.loadLogs()
    } catch (error) {
      message.error('加载初始数据失败')
      this.setState({ loading: false })
    }
  }

  /** 加载操作日志列表 */
  loadLogs = async () => {
    this.setState({ loading: true })
    try {
      const clubId = sessionStorage.getItem('club_id')
      const { tableId, operationType, dateRange, page, pageSize } = this.state

      const params = {
        clubId,
        page,
        count: pageSize,
      }
      if (tableId !== ALL_VALUE) params.tableId = tableId
      if (operationType) params.operationType = operationType
      if (dateRange[0]) params.startTimestamp = Math.floor(new Date(dateRange[0].format('YYYY-MM-DD')).getTime() / 1000)
      if (dateRange[1]) params.endTimestamp = Math.floor(new Date(dateRange[1].format('YYYY-MM-DD') + ' 23:59:59').getTime() / 1000)

      const res = await fetchOperationLogs(params)

      const resData = res && res.data ? res.data : res
      if (resData && resData.values) {
        this.setState({
          logs: resData.values || [],
          total: resData.total || 0,
          page: resData.page || page,
        })
      } else {
        this.setState({ logs: [], total: 0 })
      }
    } catch (error) {
      message.error('查询操作日志失败')
    } finally {
      this.setState({ loading: false })
    }
  }

  updateFilter = (filter) => {
    this.setState({ ...filter, page: 1 })
  }

  onDateRangeChange = (dates) => {
    this.setState({ dateRange: dates || [moment(), moment()], page: 1 })
  }

  onSearch = () => {
    this.setState({ page: 1 }, () => {
      this.loadLogs()
    })
  }

  onReset = () => {
    this.setState({
      tableId: ALL_VALUE,
      operationType: '',
      dateRange: [moment(), moment()],
      page: 1,
    })
  }

  onPageChange = (nextPage, nextPageSize) => {
    this.setState({ page: nextPage, pageSize: nextPageSize }, () => {
      this.loadLogs()
    })
  }

  /** 获取表格列配置，通过 operationTypes 查找 label 显示 */
  getColumns = () => {
    const { operationTypes } = this.state
    return [
      {
        title: '球台',
        dataIndex: 'tableName',
        key: 'tableName',
      },
      {
        title: '操作类型',
        dataIndex: 'operationType',
        key: 'operationType',
        render: (text) => {
          const option = operationTypes.find(item => item.value === text)
          return <Tag color="blue">{option ? option.label : text}</Tag>
        },
      },
      {
        title: '账号',
        dataIndex: 'account',
        key: 'account',
      },
      {
        title: '操作人',
        dataIndex: 'nickName',
        key: 'nickName',
      },
      {
        title: '操作时间',
        dataIndex: 'operationTime',
        key: 'operationTime',
        render: (text) => text ? moment(text).format('YYYY-MM-DD HH:mm:ss') : '',
      },
    ]
  }

  render() {
    const { loading, tableId, operationType, dateRange, page, pageSize, logs, total, tables, operationTypes } = this.state

    return (
      <div style={{ padding: 16 }}>
        <Card title="操作日志" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
            <FilterItem label="球台">
              <Select
                style={{ width: 200 }}
                value={tableId}
                onChange={(value) => this.updateFilter({ tableId: value })}
              >
                {tables.map((item) => (
                  <Select.Option key={item.tableId} value={item.tableId}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </FilterItem>
            <FilterItem label="操作类型">
              <Select
                style={{ width: 180 }}
                value={operationType}
                onChange={(value) => this.updateFilter({ operationType: value })}
              >
                {operationTypes.map((item) => (
                  <Select.Option key={item.value} value={item.value}>
                    {item.label}
                  </Select.Option>
                ))}
              </Select>
            </FilterItem>
            <FilterItem label="日期范围">
              <DatePicker.RangePicker
                allowClear={false}
                format="YYYY-MM-DD"
                value={dateRange}
                onChange={this.onDateRangeChange}
                disabledDate={(current) => current && (current < moment('2021-01-01') || current > moment().endOf('day'))}
              />
            </FilterItem>
            <Button type="primary" onClick={this.onSearch}>查询</Button>
            <Button onClick={this.onReset}>重置筛选</Button>
          </div>
        </Card>

        <Card title="日志列表" extra={<span style={{ color: '#999' }}>共 {total} 条日志</span>}>
          {loading ? (
            <Skeleton active />
          ) : (
            <Table
              rowKey="id"
              bordered
              columns={this.getColumns()}
              dataSource={logs}
              locale={{ emptyText: <Empty description="暂无操作日志" /> }}
              pagination={{
                current: page,
                pageSize,
                total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (t) => `共 ${t} 条`,
                onChange: this.onPageChange,
              }}
            />
          )}
        </Card>
      </div>
    )
  }
}

const FilterItem = ({ label, children }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <span style={{ width: 72, textAlign: 'right', marginRight: 8 }}>{label}：</span>
    {children}
  </div>
)

export default withRouter(ClubOperationLogs)



// WEBPACK FOOTER //
// ./src/containers/ClubOperationLogs/index.js